import { useEffect, useState } from 'react';
import { format } from 'date-fns/format';
import api from '../../../services/api';
import useAuth from '../../../hooks/useAuth';
import { PageableResponseWrapper } from '../../../domain/response-wrapper';
import { Transaction } from '../../../domain/transaction';
import { TimeFrameChart } from '../enum/Dashboard.enum';
import { TransactionData } from '../model/TransactionBarCharProps';
import { chartUtilService } from '../util/ChartUtil';

const TAB_TO_TIME_FRAME: TimeFrameChart[] = [
  TimeFrameChart.DAY,
  TimeFrameChart.WEEK,
  TimeFrameChart.MONTH,
  TimeFrameChart.YEAR,
];

interface TransactionBarChartFilters {
  idMerchant?: string[];
  idBranch?: string[];
}

/**
 * Datos de la gráfica "Transacciones Totales" (Día/Semana/Mes/Año). A diferencia del
 * enfoque anterior (traer un solo dataset y re-agruparlo en memoria para las 4
 * pestañas), cada pestaña dispara su propia consulta a `GET /transactions-view` con el
 * rango de fecha que le corresponde — el BFF rechaza rangos de más de 31 días, así que
 * "todo el año" no cabe en una sola petición: para Año se hace una petición por cada
 * mes transcurrido (ver `chartUtilService.getFetchRangesByTimeFrame`) y se combinan los
 * resultados antes de agrupar.
 */
export const useTransactionBarChartData = (filters: TransactionBarChartFilters) => {
  const { user } = useAuth();
  const [selectedTab, setSelectedTab] = useState(0);
  const [results, setResults] = useState<TransactionData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const timeFrame = TAB_TO_TIME_FRAME[selectedTab] ?? TimeFrameChart.DAY;
    const ranges = chartUtilService.getFetchRangesByTimeFrame(timeFrame);

    setLoading(true);
    setError(null);

    if (!user?.access_token) {
      setLoading(false);
      return;
    }

    const controller = new AbortController();

    Promise.all(ranges.map((range) => api.get<PageableResponseWrapper<Transaction>>('/transactions-view', {
      params: {
        page: 0,
        size: 1000,
        idMerchant: filters.idMerchant,
        idBranch: filters.idBranch,
        start: format(range.start, 'yyyy-MM-dd'),
        end: format(range.end, 'yyyy-MM-dd'),
      },
      signal: controller.signal,
    })))
      .then((responses) => {
        const transactions = responses.flatMap((response) => response.data.content ?? []);
        const model = chartUtilService.getConfigByTimeFrame(timeFrame);

        transactions.forEach((transaction) => chartUtilService.loadBarChartData(transaction, model));

        const barChartMap = chartUtilService.getBarCharMap([model]);
        setResults(barChartMap.get(timeFrame) ?? []);
        setLoading(false);
      })
      .catch((err) => {
        if (err.name !== 'CanceledError') {
          setError(err.message);
          setLoading(false);
        }
      });

    return () => controller.abort();
  }, [selectedTab, filters.idMerchant, filters.idBranch, user?.access_token]);

  return { selectedTab, setSelectedTab, results, loading, error };
};
